// # Mentor Performance Calculator
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import './mentor_perform.css';
import { teamMembers as configTeamMembers } from '../register_pay_calc';

const MentorPerformance = ({ mentorId, dateRange, onDataProcessed }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceData, setPerformanceData] = useState({
    totalRevenue: 0,
    totalStudents: 0,
    revenueByProduct: [],
    revenueOverTime: [],
    studentsByNationality: [],
    averageRating: 0,
    completionRate: 0
  });
  
  // # Store onDataProcessed in a ref to avoid dependency issues
  const onDataProcessedRef = useRef(onDataProcessed);
  
  // # Track if we've already called onDataProcessed for this data fetch
  const hasCalledCallbackRef = useRef(false);
  
  // # Track the last processed data to avoid duplicate processing
  const lastProcessedDataRef = useRef(null);
  
  // # Update ref when prop changes
  useEffect(() => {
    onDataProcessedRef.current = onDataProcessed;
  }, [onDataProcessed]);
  
  // # Memoize the team members to prevent unnecessary re-renders
  const teamMembersWithSearchTerms = useMemo(() => {
    return configTeamMembers.map(member => ({
      ...member,
      searchTerms: [member.id, member.name, member.email].filter(Boolean).map(term => term.toLowerCase())
    }));
  }, []);
  
  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
  
  // # Create a stable callback function for processing data
  const processDataCallback = useCallback((mentorPayments, uniqueStudents, studentPaymentCounts) => {
    return processMentorData(mentorPayments, uniqueStudents, studentPaymentCounts, mentorId);
  }, [mentorId]);
  
  // # Fetch and process mentor performance data
  useEffect(() => {
    // # Flag to track if component is mounted
    let isMounted = true;
    // # Reset the callback flag when dependencies change
    hasCalledCallbackRef.current = false;
    
    const fetchMentorData = async () => {
      // # Only set loading state if component is still mounted
      if (isMounted) {
        setLoading(true);
        setError(null);
      }
      
      try {
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Check if component is still mounted before continuing
        if (!isMounted) return;
        
        // # Extract all payment records related to this mentor
        let mentorPayments = [];
        const uniqueStudents = new Set();
        const studentPaymentCounts = {}; // # Track how many times each student paid this mentor
        
        // # Find the mentor in the team members array
        const mentor = teamMembersWithSearchTerms.find(m => m.id === mentorId) || {
          id: mentorId,
          name: mentorId,
          searchTerms: [mentorId.toLowerCase()]
        };
        
        // # Process each document (year_month format)
        financialSnap.docs.forEach(doc => {
          // # Document ID now contains year_month format (e.g., "2024_03")
          const docId = doc.id;
          const yearMonthData = doc.data();
          
          // # Check if payments map exists in the document
          if (yearMonthData.payments) {
            // # Extract payments from the payments map
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions with mentorship products
              if (paymentId.startsWith('payment_') && 
                  paymentData.transaction_type === 'payment' && 
                  paymentData.pay?.product && 
                  paymentData.pay.product.includes('mentorship')) {
                
                // # Check if this payment is for the specified mentor
                let isMentorMatch = false;
                
                // # Check mentor ID directly
                if (paymentData.mentor?.uid === mentorId) {
                  isMentorMatch = true;
                } 
                // # Fallback to name matching
                else {
                  const paymentMentorName = (paymentData.mentor?.mentor_name || '').toLowerCase();
                  isMentorMatch = mentor.searchTerms.some(term => 
                    paymentMentorName.includes(term)
                  );
                }
                
                if (isMentorMatch) {
                  // # Extract payment date from metadata
                  const paymentDate = new Date(paymentData.metadata?.created_at || '');
                  
                  // # Apply date range filter if provided
                  if (dateRange) {
                    const startDate = new Date(dateRange.startDate);
                    const endDate = new Date(dateRange.endDate);
                    
                    // # Set hours for proper date comparison
                    startDate.setHours(0, 0, 0, 0);
                    endDate.setHours(23, 59, 59, 999);
                    
                    // # Skip if outside date range
                    if (paymentDate < startDate || paymentDate > endDate) {
                      return;
                    }
                  }
                  
                  // # Add payment to collection with all necessary fields - directly use stored values
                  const payment = {
                    id: paymentId,
                    doc_id: docId,
                    timestamp: paymentDate,
                    egp_total: parseFloat(paymentData.pay?.egp_total) || 0,
                    egp_net: parseFloat(paymentData.pay?.egp_net) || 0,
                    product: paymentData.pay?.product || 'unknown',
                    nationality: paymentData.user?.nationality || 'Unknown',
                    email: paymentData.user?.email || '',
                    name: paymentData.user?.name || '',
                    userId: paymentData.user?.userId || '',
                    // # Directly use the shares data without recalculation
                    shares: paymentData.shares || {}
                  };
                  
                  mentorPayments.push(payment);
                  
                  // # Track unique students
                  uniqueStudents.add(payment.email);
                  
                  // # Track student payment counts
                  studentPaymentCounts[payment.email] = (studentPaymentCounts[payment.email] || 0) + 1;
                }
              }
            });
          }
        });
        
        // # Check if the data is the same as the last processed data
        const dataSignature = JSON.stringify(mentorPayments.map(p => p.id).sort());
        const isDataUnchanged = dataSignature === lastProcessedDataRef.current;
        
        // # Only process data if it's different from the last processed data
        if (!isDataUnchanged) {
          // # Update the last processed data signature
          lastProcessedDataRef.current = dataSignature;
          
          // # Process mentor data with the payments
          const processedData = processDataCallback(mentorPayments, uniqueStudents, studentPaymentCounts);
          
          // # Only update state if component is still mounted
          if (isMounted) {
            // # Set the processed data here instead of in processMentorData
            setPerformanceData(processedData);
            
            // # Call the onDataProcessed callback if provided, using the ref
            // # Only call it once per data fetch and only if it's not null
            if (onDataProcessedRef.current && !hasCalledCallbackRef.current) {
              hasCalledCallbackRef.current = true;
              // # Use a timeout to prevent excessive calls
              const paymentsCopy = [...mentorPayments]; // Create a copy to avoid reference issues
              setTimeout(() => {
                if (isMounted && onDataProcessedRef.current) {
                  try {
                    onDataProcessedRef.current(paymentsCopy);
                  } catch (callbackError) {
                    console.error('Error in onDataProcessed callback:', callbackError);
                  }
                }
              }, 100); // Add a small delay
            }
          }
        }
      } catch (error) {
        console.error('Error fetching mentor data:', error);
        // # Only update error state if component is still mounted
        if (isMounted) {
          setError('Failed to load mentor data. Please try again later.');
        }
      } finally {
        // # Only update loading state if component is still mounted
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMentorData();
    
    // # Cleanup function to set mounted flag to false when component unmounts
    return () => {
      isMounted = false;
    };
  // # Remove onDataProcessed from dependency array to prevent infinite loops
  // # Add processDataCallback to dependencies
  }, [dateRange, mentorId, teamMembersWithSearchTerms, processDataCallback]);
  
  // # Process mentor data for different metrics
  const processMentorData = (payments, uniqueStudents, studentPaymentCounts, mentorId) => {
    // # Initialize data structure
    const processedData = {
      totalRevenue: 0,
      totalStudents: uniqueStudents.size || 0,
      averageRevenuePerStudent: 0,
      revenueByProduct: [],
      revenueOverTime: [],
      studentsByNationality: [],
      revenueByNationality: [],
      averageRating: 4.7,
      completionRate: 0,
      averagePercentage: 0,
      rawPayments: payments || [],
      topStudents: []
    };

    // # Calculate total revenue from shares data
    processedData.totalRevenue = payments.reduce((sum, payment) => {
      // # Use the pre-calculated share amount for this mentor
      const mentorShare = payment.shares[mentorId]?.net_share_egp || 0;
      return sum + mentorShare;
    }, 0);
    
    // # Calculate revenue by product
    const productRevenue = {};
    payments.forEach(payment => {
      const product = payment.product || 'Unknown';
      if (!productRevenue[product]) {
        productRevenue[product] = 0;
      }
      // # Use mentor_share if available, otherwise use total amount
      const revenueAmount = payment.shares[mentorId]?.net_share_egp || 0;
      productRevenue[product] += revenueAmount;
    });
    
    // # Format revenue by product for charts
    processedData.revenueByProduct = Object.entries(productRevenue).map(([product, amount]) => ({
      product,
      amount: amount || 0,
      percentage: processedData.totalRevenue > 0 ? ((amount || 0) / processedData.totalRevenue) * 100 : 0
    }));
    
    // # Calculate revenue over time (monthly)
    const monthlyRevenue = {};
    payments.forEach(payment => {
      const date = payment.timestamp;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyRevenue[monthKey]) {
        monthlyRevenue[monthKey] = {
          month: monthKey,
          revenue: 0,
          students: new Set()
        };
      }
      
      // # Use mentor_share if available, otherwise use total amount
      const revenueAmount = payment.shares[mentorId]?.net_share_egp || 0;
      monthlyRevenue[monthKey].revenue += revenueAmount;
      
      if (payment.email) {
        monthlyRevenue[monthKey].students.add(payment.email);
      }
    });
    
    // # Format revenue over time for charts
    processedData.revenueOverTime = Object.entries(monthlyRevenue).map(([month, data]) => ({
      month,
      revenue: data.revenue,
      studentCount: data.students.size
    })).sort((a, b) => a.month.localeCompare(b.month));
    
    // # Calculate students by nationality
    const nationalityCount = {};
    payments.forEach(payment => {
      const nationality = payment.nationality || 'Unknown';
      if (!nationalityCount[nationality]) {
        nationalityCount[nationality] = {
          count: 0,
          revenue: 0,
          emails: new Set()
        };
      }
      
      if (payment.email) {
        nationalityCount[nationality].emails.add(payment.email);
      }
      
      // # Use mentor_share if available, otherwise use total amount
      const revenueAmount = payment.shares[mentorId]?.net_share_egp || 0;
      nationalityCount[nationality].revenue += revenueAmount;
    });
    
    // # Format nationality data for charts
    processedData.studentsByNationality = Object.entries(nationalityCount).map(([nationality, data]) => ({
      nationality,
      count: data.emails.size,
      revenue: data.revenue
    }));
    
    // # Calculate average revenue per student
    processedData.averageRevenuePerStudent = processedData.totalStudents > 0 
      ? processedData.totalRevenue / processedData.totalStudents 
      : 0;
    
    // # Calculate completion rate - percentage of students who made more than one payment
    const totalStudentsWithEmail = Object.keys(studentPaymentCounts).length;
    const returningStudents = Object.values(studentPaymentCounts).filter(count => count > 1).length;
    processedData.completionRate = totalStudentsWithEmail > 0 ? (returningStudents / totalStudentsWithEmail) * 100 : 0;
    
    // # Calculate top students by revenue
    const studentRevenue = {};
    payments.forEach(payment => {
      if (payment.email) {
        if (!studentRevenue[payment.email]) {
          studentRevenue[payment.email] = {
            email: payment.email,
            name: payment.name,
            nationality: payment.nationality,
            totalRevenue: 0,
            paymentCount: 0,
            lastPayment: null
          };
        }
        
        // # Use mentor_share if available, otherwise use total amount
        const revenueAmount = payment.shares[mentorId]?.net_share_egp || 0;
        studentRevenue[payment.email].totalRevenue += revenueAmount;
        studentRevenue[payment.email].paymentCount += 1;
        
        // # Track the most recent payment
        if (!studentRevenue[payment.email].lastPayment || 
            payment.timestamp > studentRevenue[payment.email].lastPayment) {
          studentRevenue[payment.email].lastPayment = payment.timestamp;
        }
      }
    });
    
    // # Convert to array and sort by revenue
    processedData.topStudents = Object.values(studentRevenue)
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 10); // Top 10 students
    
    // # Calculate revenue by nationality
    processedData.revenueByNationality = Object.entries(nationalityCount).map(([nationality, data]) => ({
      nationality,
      revenue: data.revenue,
      percentage: (data.revenue / processedData.totalRevenue) * 100
    })).sort((a, b) => b.revenue - a.revenue);
    
    // # Calculate average percentage earned
    processedData.averagePercentage = payments.length > 0
      ? payments.reduce((sum, payment) => sum + (payment.shares[mentorId]?.percentage_of_net || 0), 0) / payments.length
      : 0;
    
    // # Remove setPerformanceData from here
    return processedData;
  };
  
  // # Format currency values - memoize to prevent unnecessary re-renders
  const formatCurrency = useCallback((value) => {
    if (value === undefined || value === null) return 'E£ 0.00';
    return `E£ ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);
  
  // # Format percentage values - memoize to prevent unnecessary re-renders
  const formatPercentage = useCallback((value) => {
    if (value === undefined || value === null) return '0.0%';
    return `${Number(value).toFixed(1)}%`;
  }, []);
  
  // # Format month labels - memoize to prevent unnecessary re-renders
  const formatMonth = useCallback((monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }, []);
  
  // # Memoize the table cell renderer for mentor share to avoid unnecessary re-renders
  const renderMentorShare = useMemo(() => {
    return (payment) => {
      const hasShare = payment.shares[mentorId]?.net_share_egp > 0;
      return hasShare ? formatCurrency(payment.shares[mentorId]?.net_share_egp) : 'N/A';
    };
  }, [mentorId]);

  return (
    <div className="mentor_perform_container">
      {error && (
        <div className="mentor_perform_error">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="mentor_perform_loading">
          Loading mentor performance data...
        </div>
      ) : (
        <>
          {/* Performance Overview */}
          <div className="mentor_perform_overview">
            <div className="mentor_perform_stats-grid">
              <div className="mentor_perform_stat-card">
                <h3>Net Revenue Earned</h3>
                <p className="mentor_perform_stat-value">
                  {formatCurrency(performanceData.totalRevenue)}
                </p>
              </div>
              <div className="mentor_perform_stat-card">
                <h3>Total Students</h3>
                <p className="mentor_perform_stat-value">
                  {performanceData.totalStudents}
                </p>
              </div>
              <div className="mentor_perform_stat-card">
                <h3>Avg. Revenue per Student</h3>
                <p className="mentor_perform_stat-value">
                  {formatCurrency(performanceData.averageRevenuePerStudent)}
                </p>
              </div>
              <div className="mentor_perform_stat-card">
                <h3>Avg. Percentage Earned</h3>
                <p className="mentor_perform_stat-value">
                  {formatPercentage(performanceData.averagePercentage)}
                </p>
              </div>
              <div className="mentor_perform_stat-card">
                <h3>Completion Rate</h3>
                <p className="mentor_perform_stat-value">
                  {formatPercentage(performanceData.completionRate)}
                </p>
                <p className="mentor_perform_stat-note">
                  Students who chose this mentor again
                </p>
              </div>
            </div>
          </div>
          
          {/* Revenue by Product */}
          <div className="mentor_perform_chart-container">
            <h3>Net Revenue by Product</h3>
            <div className="mentor_perform_chart-grid">
              <div className="mentor_perform_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={performanceData.revenueByProduct}
                      dataKey="amount"
                      nameKey="product"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ product, percentage }) => `${product} (${percentage.toFixed(1)}%)`}
                    >
                      {performanceData.revenueByProduct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mentor_perform_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={performanceData.revenueByProduct}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="product" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="amount" fill="#8884d8" name="Net Revenue (EGP)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Revenue Over Time */}
          <div className="mentor_perform_chart-container">
            <h3>Net Revenue Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceData.revenueOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={formatMonth}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'revenue') return formatCurrency(value);
                    return value;
                  }}
                  labelFormatter={formatMonth}
                />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#8884d8" 
                  name="Net Revenue (EGP)" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="studentCount" 
                  stroke="#82ca9d" 
                  name="Student Count" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* Revenue by Nationality */}
          <div className="mentor_perform_chart-container">
            <h3>Net Revenue by Nationality</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData.revenueByNationality}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nationality" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'revenue') return formatCurrency(value);
                    if (name === 'percentage') return formatPercentage(value);
                    return value;
                  }}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#8884d8" name="Revenue Created (EGP)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Students by Nationality */}
          <div className="mentor_perform_chart-container">
            <h3>Students by Nationality</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData.studentsByNationality}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nationality" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'revenue') return formatCurrency(value);
                    return value;
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Student Count" />
                <Bar yAxisId="right" dataKey="revenue" fill="#82ca9d" name="Revenue Created (EGP)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Top Students */}
          <div className="mentor_perform_student-container">
            <h3>Top Students by Revenue</h3>
            <div className="mentor_perform_table-container">
              <table className="mentor_perform_table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Nationality</th>
                    <th>Net Revenue</th>
                    <th>Payments</th>
                    <th>Last Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceData.topStudents?.map((student, index) => (
                    <tr key={index}>
                      <td>{student.name || 'N/A'}</td>
                      <td title={student.email}>
                        <div className="mentor_perform_email-cell">
                          {student.email}
                        </div>
                      </td>
                      <td>{student.nationality}</td>
                      <td>{formatCurrency(student.totalRevenue)}</td>
                      <td>{student.paymentCount}</td>
                      <td>{student.lastPayment?.toLocaleDateString() || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* All Payments */}
          <div className="mentor_perform_student-container">
            <h3>All Payments</h3>
            <div className="mentor_perform_table-container">
              <table className="mentor_perform_table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Nationality</th>
                    <th>Product</th>
                    <th>Revenue Created</th>
                    <th>Mentor Share</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceData.rawPayments.map((payment, index) => (
                    <tr key={index}>
                      <td>{payment.name || 'N/A'}</td>
                      <td title={payment.email}>
                        {/* # Display email with ellipsis if too long for mobile view */}
                        <div className="mentor_perform_email-cell">
                          {payment.email}
                        </div>
                      </td>
                      <td>{payment.nationality}</td>
                      <td title={payment.product}>
                        {/* # Display product with ellipsis if too long */}
                        <div className="mentor_perform_product-cell">
                          {payment.product}
                        </div>
                      </td>
                      <td>{formatCurrency(payment.egp_total)}</td>
                      <td>{renderMentorShare(payment)}</td>
                      <td>{payment.timestamp.toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MentorPerformance;
